import { useCallback, useEffect, useState } from "react";
import GroupPresenter from "./GroupPresenter";
import * as s from "../RecordersStyled";
import TableButton from "components/atoms/buttons/TableButton";
import { Text } from "components/atoms/text/Text";
import { COLORS } from "styles/colors";
import { useLocation, useNavigate } from "react-router-dom";
import {
  RecorderGroup,
  RecorderGroupListResponse,
} from "api/interfaces/recorderInterface.interface";
import * as mediaQuery from "components/MediaQuery";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import {
  BasePagination,
  PageTotalStatus,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { useQuery } from "react-query";
import { getRecorderGroupList } from "api/recorderAPI";
import { isCheckPermission } from "utils/AccountUtil";
import RBACWrapper from "components/blocks/function/RBACWrapper";
import { isNull } from "lodash";

import { TableColumnsType } from "antd";
import {
  changeSortOrderAntd,
  onSortAntd,
} from "components/atoms/table/AntdTable";
import { ColumnType } from "antd/es/table";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import { useMenu } from "hook/useMenu";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";

const initialQueryInfo = baseQueryInfo;

// TODO Recorder Group API 구현 후 개발보완 필요.
export function RecordersContainer(): JSX.Element {
  const location = useLocation();
  const isBigScreen = mediaQuery.useIsBigScreen();
  const { queryNavigate } = useMenu({});

  const [isAddModal, setIsAddModal] = useState<boolean>(false);
  const [isEditModal, setIsEditModal] = useState<boolean>(false);

  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const [recGroup, setRecGroup] = useState<RecorderGroup[]>([]);
  const [currGroup, setCurrGroup] = useState<RecorderGroup>();

  const [listTotal, setListTotal] = useState<number>(0);

  const [recGroupQueryInfo, setRecGroupQueryInfo] = useState<BasePagination>({
    ...initialQueryInfo,
  });
  const queryKey = useQueryKey(recGroupQueryInfo);
  const [pending, setPending] = useState<boolean>(true);
  const isDesktop = mediaQuery.useIsDesktop();
  const isMobile = mediaQuery.useIsMobile();

  const [isRowClickEditable, setIsRowClickEditable] = useState<boolean>(
    isCheckPermission("manageRecordersAndGroups", selectedAccount)
  );

  useEffect(() => {
    if (selectedAccount) {
      setIsRowClickEditable(
        isCheckPermission("manageRecordersAndGroups", selectedAccount)
      );
    }
  }, [selectedAccount]);

  useEffect(() => {
    const changeParam = new URLSearchParams(location.search);
    if (changeParam.size > 0) {
      setRecGroupQueryInfo((info) => {
        return {
          ...info,
          pageNum: isNull(changeParam.get("pageNum"))
            ? 0
            : Number(changeParam.get("pageNum")),
          pageLimit: isNull(changeParam.get("pageLimit"))
            ? 20
            : Number(changeParam.get("pageLimit")),
          keyword: isNull(changeParam.get("keyword"))
            ? ""
            : (changeParam.get("keyword") as string),
          sortType: isNull(changeParam.get("sortType"))
            ? "name"
            : (changeParam.get("sortType") as string),
          sortDirection: isNull(changeParam.get("sortDirection"))
            ? SORT_DIRECTION.ASC
            : (changeParam.get("sortDirection") as SORT_DIRECTION),
        };
      });
    } else {
      setRecGroupQueryInfo((info) => {
        return {
          ...initialQueryInfo,
        };
      });
    }
  }, [location]);

  const [status, setStatus] = useState<PageTotalStatus>({
    totalTitle: "Total Groups",
    totalValue: 0,
  });
  const onHeaderCell = (column: ColumnType<RecorderGroup>) => {
    return {
      onClick: () => {
        if (column.key) {
          setRecGroupQueryInfo((info) => {
            return {
              ...info,
              sortType: column.key as string,
              sortDirection: onSortAntd(info.sortDirection),
              pageNum: 0,
            };
          });
        }
      },
    };
  };
  const columns: TableColumnsType<RecorderGroup> = [
    {
      title: "Name",
      sorter: true, // 정렬 활성화,
      sortOrder: changeSortOrderAntd(recGroupQueryInfo, "name"),
      onHeaderCell: onHeaderCell,
      key: "name",
      defaultSortOrder: "ascend",
      render: (row) => row.name,
    },
    {
      title: "Host",
      sorter: true,
      sortOrder: changeSortOrderAntd(recGroupQueryInfo, "createBy"),
      onHeaderCell: onHeaderCell,
      key: "createBy",
      render: (value, row) => (
        <s.Row>
          <Text fontSize={14} isDeleted={!row.hostValidation}>
            {row.username}
          </Text>
          <mediaQuery.Default>
            <Text fontSize={13} isDeleted={!row.hostValidation}>
              {isDesktop && `(${row.email})`}
              {row.hostValidation ? "" : ` - Deactivated User`}
            </Text>
          </mediaQuery.Default>
        </s.Row>
      ),

      ellipsis: true,
      width: isMobile ? 150 : "30%",
    },
    {
      title: "Recorders",
      render: (value, row) => row.recorderCount,
      align: "center",
      width: isBigScreen ? 200 : 100,
    },
    {
      title: "Description",
      sorter: true,
      sortOrder: changeSortOrderAntd(recGroupQueryInfo, "description"),
      onHeaderCell: onHeaderCell,
      key: "description",
      render: (value, row) => row.description,
      responsive: ["xl"],
      width: isBigScreen ? "20%" : 250,
      ellipsis: true,
    },
    // edit 버튼
    {
      title: "",
      width: 60,
      render: (value, row) => (
        <RBACWrapper requiredPermissions="manageRecordersAndGroups">
          <s.Row className="table-button-wrapper">
            <span className="row-click-button">
              <TableButton
                label="Edit"
                onClick={() => {
                  setCurrGroup(row);
                  onModal("edit");
                }}
                data-testid="recorders-groups-groups-table-button-set-curr-group"
              />
            </span>
          </s.Row>
        </RBACWrapper>
      ),

      align: "right",
    },
  ];

  const onModal = (type: string, row?: RecorderGroup) => {
    if (type === "add") {
      // if (isAddModal) {
      //   queryClient.invalidateQueries("recorderGroup");
      // }
      setIsAddModal(!isAddModal);
    }
    if (type === "edit") {
      // if (isEditModal) {
      //   queryClient.invalidateQueries("recorderGroup");
      // }
      setIsEditModal(!isEditModal);
    }
    //queryClient.invalidateQueries("recorderGroup");
    refetch();
  };

  const onClickEdit = (row: RecorderGroup) => {
    if (isCheckPermission("manageRecordersAndGroups", selectedAccount)) {
      setCurrGroup(row);
      onModal("edit");
    }
  };

  const { refetch } = useQuery(
    ["recorderGroup", queryKey, selectedAccount.accountId],
    () =>
      getRecorderGroupList({
        payload: recGroupQueryInfo,
        accountId: selectedAccount.accountId,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: RecorderGroupListResponse) => {
        if (res.result === undefined) {
          setRecGroup([]);
          setListTotal(0);
          setStatus({
            totalTitle: "Total Groups",
            totalValue: 0,
          });
          setRecGroupQueryInfo((info) => {
            return {
              ...initialQueryInfo,
            };
          });
          return;
        }
        setRecGroup(res.result);
        setListTotal(res.result.length);
        setStatus({
          totalTitle: "Total Groups",
          totalValue: res.page.total,
        });
        setRecGroupQueryInfo((info) => {
          return {
            ...info,
            ...res.page,
          };
        });
      },
      onError: (e: any) => {
        setRecGroup([]);
        setListTotal(0);
        setStatus({
          totalTitle: "Total Groups",
          totalValue: 0,
        });
        setRecGroupQueryInfo((info) => {
          return {
            ...initialQueryInfo,
          };
        });
      },
      onSettled: (e: any) => {
        setPending(false);
      },
    }
  );
  useStompDebouncedRefetch({
    stompKey: [
      "recorderGroupUpdated",
      "recorderListUpdated",
    ] as StompDateSignalKey[],
    refetch: refetch,
  });

  const onSuccessDelete = () => {
    queryNavigate({
      to: location.pathname,
      queryInfo: {
        ...initialQueryInfo,
      },
    });
  };

  const onChangePage = useCallback(
    (page: number, totalRows: number) => {
      console.log(page, totalRows);
      if (page !== recGroupQueryInfo.pageNum + 1) {
        queryNavigate({
          to: location.pathname,
          queryInfo: {
            pageLimit: recGroupQueryInfo.pageLimit,
            pageNum: page - 1,
            keyword: recGroupQueryInfo.keyword as string,
            sortType: recGroupQueryInfo.sortType,
            sortDirection: recGroupQueryInfo.sortDirection as SORT_DIRECTION,
          },
        });
        setPending(true);
        // setRecGroupQueryInfo((info) => {
        //   return { ...info, pageNum: page as number };
        // });
      }
    },
    [
      location.pathname,
      queryNavigate,
      recGroupQueryInfo.keyword,
      recGroupQueryInfo.pageLimit,
      recGroupQueryInfo.pageNum,
      recGroupQueryInfo.sortDirection,
      recGroupQueryInfo.sortType,
    ]
  );

  const onClickSearch = useCallback(
    (keyword: string) => {
      if (recGroupQueryInfo.keyword !== keyword) {
        queryNavigate({
          to: location.pathname,
          queryInfo: {
            pageLimit: recGroupQueryInfo.pageLimit,
            pageNum: 0,
            keyword: keyword,
            sortType: recGroupQueryInfo.sortType,
            sortDirection: recGroupQueryInfo.sortDirection as SORT_DIRECTION,
          },
        });
      } else {
        refetch();
      }
      setPending(true);
      // setRecGroupQueryInfo((query) => {
      //   return {
      //     ...query,
      //     keyword,
      //   };
      // });
      // refetch();
    },
    [
      location.pathname,
      queryNavigate,
      recGroupQueryInfo.keyword,
      recGroupQueryInfo.pageLimit,
      recGroupQueryInfo.sortDirection,
      recGroupQueryInfo.sortType,
      refetch,
    ]
  );

  return (
    <GroupPresenter
      onModal={onModal}
      isAddModal={isAddModal}
      isEditModal={isEditModal}
      columns={columns}
      status={status}
      data={recGroup}
      selectGroup={currGroup}
      onClickEdit={onClickEdit}
      paginationInfo={recGroupQueryInfo}
      totalRows={listTotal}
      onChangePage={onChangePage}
      onClickSearch={onClickSearch}
      isRowClickEditable={isRowClickEditable}
      initialPending={pending}
      onSuccessDelete={onSuccessDelete}
    />
  );
}
